import React, { useEffect, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
  Row,
  CellContext,
} from '@tanstack/react-table';
import { useApi } from '../../hooks/useApi';
import DealsUtm from '../../api/analytics/dto/DealsUtmResp';
import './TableUtmDeal.css';
import { useStores } from '../../hooks/useStore';

type UTMRow = {
  utm_value: string;
  leads: number;
  not_started: number;
  in_progress: number;
  no_answered: number;
  leads_pool: number;
  completed: number;
  failed: number;
  parent?: string;
  name?: string;
  subRows?: UTMRow[];
};

const levelByDepth = [
  'Utm Source',
  'Utm Medium',
  'Utm Campaign',
  'Utm Term',
  'Utm Content',
];

/* ---------- вспомогалка для next-parent ---------- */
const getNextParent = (cur?: string): string | undefined => {
  if (!cur) {
    return 'utm_source';
  }
  switch (cur) {
    case 'utm_source':
      return 'utm_medium';
    case 'utm_medium':
      return 'utm_campaign';
    case 'utm_campaign':
      return 'utm_term';
    case 'utm_term':
      return 'utm_content';
    default:
      return undefined;
  }
};

const DrilldownTable: React.FC<{ startDate: string; endDate: string }> = ({
  startDate,
  endDate,
}) => {
  const api = useApi();
  const store = useStores();

  const [data, setData] = useState<UTMRow[]>([]);

  /* ---------- запрос данных ---------- */
  const loadData = async (
    parent?: string,
    name?: string,
  ): Promise<UTMRow[]> => {
    if (!startDate || !endDate) {
      return [];
    } // ещё нет дат – выходим
    store.notificationsStore.showPreloader();
    const { body } = await api.analytics.getDealsUtm(
      startDate,
      endDate,
      parent ?? null,
      name ?? null,
    );
    store.notificationsStore.hidePreloader();

    if (!body) {
      return [];
    }

    return body.map((i: DealsUtm) => ({
      utm_value: i.utm_value || 'Без названия',
      leads: i.leads,
      not_started: i.not_started,
      in_progress: i.in_progress,
      no_answered: i.no_answered,
      leads_pool: i.leads_pool,
      completed: i.completed,
      failed: i.failed,
      parent,
      name,
    }));
  };

  /* ---------- начальная загрузка ---------- */
  useEffect(() => {
    (async () => setData(await loadData()))();
  }, [startDate, endDate]);

  /* ---------- lazy-expand ---------- */
  /* ---------- lazy-expand ---------- */
  const onExpand = async (row: Row<UTMRow>) => {
    if (row.original.subRows) {
      return;
    } // уже загружали

    const kids = await loadData(
      getNextParent(row.original.parent),
      row.original.utm_value,
    );

    setData((prev) => {
      const recursive = (arr: UTMRow[]): UTMRow[] =>
        arr.map((r) => {
          if (r === row.original) {
            return { ...r, subRows: kids };
          }
          if (r.subRows) {
            return { ...r, subRows: recursive(r.subRows) };
          }
          return r;
        });

      return recursive(prev);
    });
  };

  /* ---------- столбцы ---------- */
  const columns = [
    {
      /* здесь будет и «+», и сама метка */
      header: 'Метка',
      accessorKey: 'utm_value',
      cell: ({ row, getValue }: CellContext<UTMRow, unknown>) => (
        <div
          className="cell-wrapper"
          style={{ paddingLeft: row.depth * 16 }} /* лесенка */
        >
          {row.getCanExpand() && row.original.utm_value && (
            <button
              className="exp-btn"
              onClick={async (e) => {
                e.stopPropagation();
                if (!row.getIsExpanded()) {
                  await onExpand(row);
                }
                row.toggleExpanded();
              }}
            >
              {row.getIsExpanded() ? '−' : '+'}
            </button>
          )}
          {String(getValue())}
          <span className="level-tag">{levelByDepth[row.depth]}</span>
        </div>
      ),
    },

    {
      header: 'Лиды',
      accessorKey: 'leads',
      cell: (info: CellContext<UTMRow, unknown>) => (
        <span className="num-cell">{info.getValue<number>()}</span>
      ),
    },

    {
      header: 'Не взяты',
      accessorKey: 'not_started',
      cell: (info: CellContext<UTMRow, unknown>) => (
        <span className="num-cell">{info.getValue<number>()}</span>
      ),
    },

    {
      header: 'В работе',
      accessorKey: 'in_progress',
      cell: (info: CellContext<UTMRow, unknown>) => (
        <span className="num-cell">{info.getValue<number>()}</span>
      ),
    },

    {
      header: 'НДЗ',
      accessorKey: 'no_answered',
      cell: (info: CellContext<UTMRow, unknown>) => (
        <span className="num-cell">{info.getValue<number>()}</span>
      ),
    },

    {
      header: 'Заделы',
      accessorKey: 'leads_pool',
      cell: (info: CellContext<UTMRow, unknown>) => (
        <span className="num-cell">{info.getValue<number>()}</span>
      ),
    },
    {
      header: 'Реализовано',
      accessorKey: 'completed',
      cell: (info: CellContext<UTMRow, unknown>) => (
        <span className="num-cell">{info.getValue<number>()}</span>
      ),
    },

    {
      header: 'Не реализовано',
      accessorKey: 'failed',
      cell: (info: CellContext<UTMRow, unknown>) => (
        <span className="num-cell">{info.getValue<number>()}</span>
      ),
    },
  ];

  /* ---------- таблица ---------- */
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (r) => r.subRows ?? [],
    getRowCanExpand: () => true,
    getRowId: (r) => [r.parent, r.utm_value].filter(Boolean).join('::'),
  });

  /* ---------- рендер ---------- */
  return (
    <div>
      <table className="utm-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th key={h.id}>
                  {flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell, idx) => (
                <td
                  key={cell.id}
                  className={idx === 1 ? `tree-cell level-${row.depth}` : ''}
                  // style={
                  //   idx === 1 ? { paddingLeft: row.depth * 16 } : undefined
                  // }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DrilldownTable;
